/**
 * FEAT-031 / S-212 — Tenant analytics dashboard.
 *
 * Workspace-scoped usage view: piece summary cards + token totals broken down
 * by workspace, pipeline stage, and key source over a selectable date range.
 * Calls GET /analytics/summary; the API enforces tenant scoping from the JWT.
 */

import { stageLabel } from "@/lib/stage-labels";
import { useMemo, useState } from "react";
import { useApiQuery } from "@/lib/api";
import {
  pipelineHealth,
  HEALTH_LABEL,
  HEALTH_HINT,
} from "@/lib/pipeline-health";

import { formatHours, PHASE_AGENT } from "./analytics-format.js";
import { MAIN_PIPELINE_PHASES } from "@shared/fiction-phases";
import {
  agentForStop,
  markedAgent,
  ALWAYS_WAVED_MIN_STOPS,
  type AgentStopRow,
} from "@shared/agent-stops";
import {
  voiceDimensions,
  VOICE_AGGREGATE_GATE,
  VOICE_AGGREGATE_MAX,
  VOICE_CATEGORY_FLOOR,
  VOICE_FLOOR_PERCENT,
} from "@shared/voice-scores";

interface SummaryResponse {
  period: { from: string; to: string };
  /** S-383 — median hours per step. `kind` separates the agent's own time
   *  from the wait on a person, which is the entire point of the chart. */
  stage_wait?: {
    phase: string;
    kind: string;
    median_hours: number;
    samples: number;
  }[];
  /** S-394 — per agent that stopped a piece: how often, and how often the
   *  person then changed nothing. The screen's one actionable judgement. */
  agent_stops?: {
    phase: string;
    reason: string | null;
    stops: number;
    waved_through: number;
  }[];
  /** S-384 — group means over the range. Null dimensions mean nothing in the
   *  range was scored, which is a different answer from zero. */
  voice?: {
    scored: number;
    under_floor: number;
    aggregate: number | null;
    dimensions: Record<string, number | null>;
  };
  pieces: {
    total: number;
    by_status: { status: string; count: number }[];
    by_agencyclient: { agencyclient_id: number; name: string; count: number }[];
  };
  tokens: {
    total_input: number;
    total_output: number;
    by_agencyclient: {
      agencyclient_id: number;
      name: string;
      input: number;
      output: number;
    }[];
    by_stage: { stage: string; input: number; output: number }[];
    by_key_source: { key_source: string; input: number; output: number }[];
  };
}

const KEY_SOURCE_LABELS: Record<string, string> = {
  platform: "Platform key",
  byoapi: "Your keys (BYOAPI)",
  // BUG-087 — the operator's own subscription, billed to nobody. GROUP BY
  // key_source already surfaced this value with no code change; only the
  // label was missing.
  subscription: "Your subscription",
};

const RANGES = [
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
] as const;

const fmt = (n: number) => n.toLocaleString();
const DAY_MS = 24 * 60 * 60 * 1000;

function rangeToParams(days: number): string {
  const to = new Date();
  const from = new Date(to.getTime() - days * DAY_MS);
  return `from=${from.toISOString()}&to=${to.toISOString()}`;
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
}

function countByStatus(rows: { status: string; count: number }[], status: string): number {
  return rows.find((r) => r.status === status)?.count ?? 0;
}

export function AnalyticsView() {
  const [rangeKey, setRangeKey] = useState<(typeof RANGES)[number]["key"]>("30");
  const days = RANGES.find((r) => r.key === rangeKey)?.days ?? 30;
  const params = useMemo(() => rangeToParams(days), [days]);

  const { data, isLoading, error } = useApiQuery<SummaryResponse>(
    ["analytics-summary", rangeKey],
    `/analytics/summary?${params}`
  );

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <header className="settings-header">
          <div>
            <h1 className="settings-title">Analytics</h1>
            <p className="settings-sub">Piece and token usage for your workspace.</p>
          </div>
          <div className="analytics-range" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`view-btn ${rangeKey === r.key ? "is-active" : ""}`}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </header>

        {isLoading && <div className="panel-empty">Loading…</div>}
        {error && (
          <div className="panel-empty">Could not load analytics. Try again.</div>
        )}

        {data && !isLoading && (
          <>
            {/* S-379 — the question this screen answers is whether the
                machine is running, so the pipeline's state leads and the
                token totals follow as the cost report they are. */}
            <section className="analytics-health">
              {pipelineHealth(data.pieces.by_status).map((h) => (
                <div key={h.bucket} className={`health-card is-${h.bucket}`}>
                  <span className="health-value">{fmt(h.count)}</span>
                  <span className="health-label">{HEALTH_LABEL[h.bucket]}</span>
                  <span className="health-hint">{HEALTH_HINT[h.bucket]}</span>
                </div>
              ))}
            </section>

            {data.stage_wait && <StageWaitSection rows={data.stage_wait} />}
            {data.agent_stops && <AgentStopsSection rows={data.agent_stops} />}

            {data.voice && <VoiceFidelitySection voice={data.voice} />}

            <section className="analytics-stats">
              <div className="stat-card">
                <span className="stat-label">Total pieces</span>
                <span className="stat-value">{fmt(data.pieces.total)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Published</span>
                <span className="stat-value">
                  {fmt(countByStatus(data.pieces.by_status, "published"))}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Input tokens</span>
                <span className="stat-value">{fmt(data.tokens.total_input)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Output tokens</span>
                <span className="stat-value">{fmt(data.tokens.total_output)}</span>
              </div>
            </section>

            <AnalyticsTable
              title="Pieces by status"
              headers={["Status", "Count"]}
              rows={data.pieces.by_status.map((r) => [
                statusLabel(r.status),
                fmt(r.count),
              ])}
              emptyText="No pieces in this range."
            />

            <AnalyticsTable
              title="Tokens by workspace"
              headers={["Workspace", "Input", "Output"]}
              rows={data.tokens.by_agencyclient.map((r) => [
                r.name,
                fmt(r.input),
                fmt(r.output),
              ])}
              emptyText="No token usage in this range."
            />

            <AnalyticsTable
              title="Tokens by stage"
              headers={["Stage", "Input", "Output"]}
              rows={data.tokens.by_stage.map((r) => [
                stageLabel(r.stage),
                fmt(r.input),
                fmt(r.output),
              ])}
              emptyText="No token usage in this range."
            />

            <AnalyticsTable
              title="Tokens by key source"
              headers={["Source", "Input", "Output"]}
              rows={data.tokens.by_key_source.map((r) => [
                KEY_SOURCE_LABELS[r.key_source] ?? r.key_source,
                fmt(r.input),
                fmt(r.output),
              ])}
              emptyText="No token usage in this range."
            />
          </>
        )}
      </div>
    </main>
  );
}

interface TableProps {
  title: string;
  headers: string[];
  rows: string[][];
  emptyText: string;
}

export function AnalyticsTable({ title, headers, rows, emptyText }: TableProps) {
  return (
    <div className="panel-body analytics-section">
      <div className="panel-header">
        <h3>{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="panel-empty">{emptyText}</div>
      ) : (
        <table className="token-table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={h} className={i === 0 ? undefined : "token-num"}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className={ci === 0 ? undefined : "token-num"}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * S-384 — voice fidelity across the range.
 *
 * **Group means, deliberately.** A single piece's score carries σ≈4.7 — far too
 * noisy to read one piece off — but the mean of N pieces has a standard error of
 * σ/√N, so 26 pieces puts it near 0.9. This chart therefore answers "does the
 * prose in this range sit above or below the line", which the instrument
 * supports, and never implies per-piece precision, which it does not. That is
 * S-392's argument, and it is why this section says how many pieces it rests on
 * rather than leaving the reader to assume.
 *
 * **Every number here is read, never typed.** The handoff README names a gate of
 * 85 and a floor of 70; those were its stand-ins from before FEAT-084 chose a
 * scale, and the shipped values are a 0–10 dimension with a floor of 6 and a
 * 0–40 aggregate gated at 28. The design's own rule settles it: "every headline
 * number is derived from the data it describes. None are typed." So the bars and
 * the floor line are positioned by one function of the same constants — which is
 * the drift the story was written to prevent.
 */
function VoiceFidelitySection({
  voice,
}: {
  voice: NonNullable<SummaryResponse["voice"]>;
}) {
  if (voice.scored === 0) {
    return (
      <section className="analytics-section-block">
        <h2 className="analytics-h2">Voice fidelity</h2>
        <p className="analytics-note">
          Nothing in this range has been scored. Pieces are scored after the
          Editor runs; an older piece can be scored from its Voice &amp; style
          report.
        </p>
      </section>
    );
  }

  // The endpoint already returns the snake_case keys voiceDimensions reads
  // (voice_match_score, rhythm_score, …), so this needs no remapping — and a
  // no-op converter in the middle would only invite someone to trust it.
  const dims = voiceDimensions(voice.dimensions);

  return (
    <section className="analytics-section-block">
      <h2 className="analytics-h2">Voice fidelity</h2>
      <p className="analytics-note">
        Mean of {voice.scored} scored piece{voice.scored === 1 ? "" : "s"}.
        {voice.scored < 10 ? (
          <> Too few to read much into — the mean settles as the count grows.</>
        ) : null}
      </p>

      {voice.aggregate != null && (
        <p className="voice-an-aggregate">
          <strong>{voice.aggregate}</strong>
          <span>/{VOICE_AGGREGATE_MAX}</span>
          <em>gate {VOICE_AGGREGATE_GATE}</em>
        </p>
      )}

      <div className="voice-an-bars">
        {dims.map((d) => (
          <div key={d.key} className="voice-an-row">
            <span className="voice-an-label">{d.label}</span>
            <span className="voice-an-track">
              <span
                className={d.under ? "is-under" : "is-pass"}
                style={{ width: `${d.percent}%` }}
              />
              {/* The floor line and every bar are positioned by the same
                  constant, so the line cannot drift from the bars it gates. */}
              <span
                className="voice-an-floor"
                style={{ left: `${VOICE_FLOOR_PERCENT}%` }}
                aria-hidden
              />
            </span>
            <span className="voice-an-value">
              {d.score == null ? "\u2014" : d.score}
            </span>
          </div>
        ))}
      </div>

      {voice.under_floor > 0 && (
        <p className="analytics-note">
          {voice.under_floor} of {voice.scored} had a dimension under{" "}
          {VOICE_CATEGORY_FLOOR}. The total can look acceptable while one
          dimension is broken, which is why the floor exists alongside it.
        </p>
      )}
    </section>
  );
}

/**
 * S-383 — where the time actually goes.
 *
 * The design's own fixture is the finding, not an illustration: every agent step
 * finishes inside three hours while "Your review" sits at 41. The pipeline is
 * not slow — the queue is. So the human row is not one bar among nine; it is the
 * answer, and the chart is scaled to whatever the slowest step is so the shape
 * of that gap survives contact with real data.
 *
 * Empty until pieces run through the instrumented route. There is no history to
 * backfill from — nothing recorded phase boundaries before this — and the screen
 * says so rather than drawing a chart out of nothing.
 */
function StageWaitSection({
  rows,
}: {
  rows: NonNullable<SummaryResponse["stage_wait"]>;
}) {
  if (rows.length === 0) {
    return (
      <section className="analytics-section-block">
        <h2 className="analytics-h2">Where the time goes</h2>
        <p className="analytics-note">
          Nothing measured in this range yet. Step timings are recorded from the
          moment a piece moves between phases, so this fills in as pieces run —
          there is no history to recover.
        </p>
      </section>
    );
  }

  const max = Math.max(...rows.map((r) => r.median_hours), 0.1);
  const agent = rows.filter((r) => r.kind === "agent");
  const agentTotal = agent.reduce((n, r) => n + r.median_hours, 0);
  const human = rows.find((r) => r.kind === "human");

  return (
    <section className="analytics-section-block">
      <h2 className="analytics-h2">Where the time goes</h2>
      <p className="analytics-note">Median wait at each step, in hours.</p>

      <div className="wait-rows">
        {rows.map((r) => (
          <div key={`${r.kind}:${r.phase}`} className="wait-row">
            <span className="wait-label">{PHASE_AGENT[r.phase] ?? r.phase}</span>
            <span className="wait-track">
              <span
                className={r.kind === "human" ? "is-human" : "is-agent"}
                style={{ width: `${(r.median_hours / max) * 100}%` }}
              />
            </span>
            <span className="wait-value">{formatHours(r.median_hours)}</span>
          </div>
        ))}
      </div>

      {/* Only drawn when both halves have been measured — the comparison is the
          point, and half of it is not a weaker version of it, it is a different
          claim. */}
      {human && agent.length > 0 && (
        <p className="analytics-note wait-verdict">
          Every agent step together accounts for {formatHours(agentTotal)}.{" "}
          {human.median_hours > agentTotal ? (
            <>
              Your review is {formatHours(human.median_hours)} of that on its
              own — the pipeline is not slow, the queue is.
            </>
          ) : (
            <>
              Your review adds {formatHours(human.median_hours)} on top of it.
            </>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * S-394 — which agent's stops you always wave through.
 *
 * The design's own words: "an agent whose stops you always wave through is
 * costing you time it has not earned — that is the only judgement on this screen
 * the user can act on." One row is marked, at most, and the threshold for
 * marking it lives in `@shared/agent-stops` because it is a claim about the data
 * rather than a detail of how the table is drawn.
 *
 * Empty until decisions accumulate. Nothing before migration 0067 distinguished
 * a wave-through from a revision, so there is no history to recover — the same
 * answer the step-timing chart gives, and for the same reason.
 */
function AgentStopsSection({
  rows,
}: {
  rows: NonNullable<SummaryResponse["agent_stops"]>;
}) {
  const named: AgentStopRow[] = rows.map((r) => ({
    agent: agentForStop(r.phase, MAIN_PIPELINE_PHASES),
    reason: r.reason,
    stops: r.stops,
    waved_through: r.waved_through,
  }));
  const marked = markedAgent(named);

  return (
    <section className="analytics-section-block">
      <h2 className="analytics-h2">Which stops earned the interruption</h2>
      {named.length === 0 ? (
        <p className="analytics-note">
          No decisions recorded in this range yet. Each time an agent stops a
          piece and you either continue or send it back, that choice is counted
          here — so this fills in as you work. There is no history to recover.
        </p>
      ) : (
        <>
          <p className="analytics-note">
            How often each agent stopped a piece, and how often you carried on
            without changing anything.
          </p>

          <div className="stop-rows">
            {named.map((r) => (
              <div
                key={r.agent}
                className={`stop-row${r.agent === marked ? " is-marked" : ""}`}
              >
                <span className="stop-agent">
                  {r.agent}
                  {r.reason && <span className="stop-reason"> · {r.reason}</span>}
                </span>
                <span className="stop-count">
                  {r.waved_through} of {r.stops} waved through
                </span>
              </div>
            ))}
          </div>

          {marked ? (
            <p className="analytics-note stop-verdict">
              You have never changed anything after {marked} stopped a piece.
              That stop is costing you time it has not earned — consider whether
              it needs to interrupt you at all.
            </p>
          ) : (
            <p className="analytics-note">
              No agent has earned a flag yet. It takes {ALWAYS_WAVED_MIN_STOPS}{" "}
              stops, every one of them waved through, before that is a pattern
              rather than a coincidence.
            </p>
          )}
        </>
      )}
    </section>
  );
}
